import {
  Component, ElementRef,
  EventEmitter, HostListener,
  Input, OnDestroy,
  OnInit, Output, Renderer2,
  ViewChild
} from '@angular/core';
import { AbstractFieldComponent } from '../../abstract/abstract-field.component';
import { SelectContentText } from '../multiselect/material-multiselect.component';
import { isNullOrUndefined } from 'util';
import { SelectLabelService } from './label-options';
import { Memoize } from '../../../utils/memoize';
import { FieldType } from '../../../models/field-type';
import { LanguageService } from '../../../services/language.service';
import { NgSelectComponent } from '@ng-select/ng-select';
import {MatDialog, MatDialogRef} from '@angular/material';
import {
  DynamicDictionaryEvent,
  SelectDialogComponent
} from '../../dialogs/select-dialog/select-dialog.component';
import {isArray, isCarOne, isIOS, isMobile, isPromise} from '../../../../core/utils/utils';
import { isNotNullOrUndefined } from 'codelyzer/util/isNotNullOrUndefined';
import { TranslateService } from '@ngx-translate/core';
import { VehicleModelConsts } from '../../../../core/consts/vehicle-model.consts';
import {DeviceService} from '../../../services/device.service';
import {FormFieldService} from '../../../services/form-field.service';
import * as _ from 'lodash';
import {FieldOptionLabel} from '../../../models/field-option';

@Component({
  selector: 'app-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss']
})
export class SelectComponent extends AbstractFieldComponent implements OnInit, OnDestroy {

  @ViewChild('select') select: NgSelectComponent;
  @Input() data;
  @Input() translateLabel = false;
  @Input() selectDialogRef: MatDialogRef<SelectDialogComponent>;

  @Output() opened: EventEmitter<void> = new EventEmitter<void>();
  @Output() selectDialogRefUpdate: EventEmitter<MatDialogRef<SelectDialogComponent>> = new EventEmitter<MatDialogRef<SelectDialogComponent>>();

  fieldType = FieldType;
  placeholder: string;
  loading: boolean = false;
  multiple: boolean = true;
  closeOnSelect: boolean = true;
  isFocused: boolean = false;
  hideLabel: boolean = false;
  clearable: boolean = false;
  animation: boolean = false;
  searchable: boolean = true;
  clearOnClose: boolean = false;
  saveViaButton: boolean = false;
  autoSelectOnSearch: boolean = false;
  dropdownPosition: string = 'auto';
  loadingText: string = 'SELECT_COMPONENT.loadingText';
  selectSearchVisible: boolean = false;
  items;
  itemsEvent = new EventEmitter();
  dynamicDictionaryEvent: EventEmitter<DynamicDictionaryEvent> = new EventEmitter();
  notFoundText: string = '';
  typeToSearchText: string = null;
  selectSearchText: string = 'PLACEHOLDERS.search';
  animationEnabled: boolean = true;
  customIcon: string;

  $closeEvent: EventEmitter<any> = new EventEmitter<any>();
  $propagateValues: EventEmitter<any> = new EventEmitter<any>();

  labelService: SelectLabelService = new SelectLabelService();

  prevTmpControlValues = [];
  templateOptions: TemplateOptions = {
    eurotax: VehicleModelConsts.VEHICLE_CODE_EUROTAX,
    infoexpert: VehicleModelConsts.VEHICLE_CODE_INFOEKSPERT,
    model: VehicleModelConsts.MODEL_CODE,
  };
  tooltipOutlined: boolean = isCarOne();

  private valueChanges$;
  private openedProgrammatically = false;

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.initSelectSearchVisible(event.target.innerWidth);
  }

  constructor(public formFieldService: FormFieldService,
              private dialog: MatDialog,
              private ts: TranslateService,
              private languageService: LanguageService,
              private deviceService: DeviceService,
              private translateService: TranslateService,
              private renderer: Renderer2,
              private elementRef: ElementRef) {
    super(formFieldService);
  }

  ngOnInit() {
    super.ngOnInit();
    this.placeholder = this.fieldConfig.caption;

    this.initItems();
    this.initMultiple();
    this.initCloseOnSelect();
    this.initClearable();
    this.initSearchable();
    this.initDropdownPosition();
    this.initClearOnClose();
    this.initNotFoundText();
    this.initAutoSelectOnSearch();
    this.initTypeToSearchText();
    this.initLoadingEvent();
    this.initAnimationEnabled();
    this.initCustomIcon();
    this.initSelectSearchVisible(window.innerWidth);

    this.initValueChangeControl();
    this.attachSearchFn();
    this.handleStatusChanges();
  }

  ngOnDestroy(): void {
    if (isNotNullOrUndefined(this.valueChanges$)) {
      this.valueChanges$.unsubscribe();
    }
  }

  @Memoize()
  getConcatLabel(item, label): string {
    if (label && isNotNullOrUndefined(item)) {
      let labelArray = label.label;
      let labelConfig = label.labelConfig;
      let resultLabel = '';

      if (this.translateLabel) {
        return this.translateService.instant(item[labelArray]);
      } else {
        resultLabel = this.labelService.concatLabel(labelArray, resultLabel, item, labelConfig);
        return Array.isArray(resultLabel) ? this.getLabelForArray(item, resultLabel) : resultLabel;
      }
    } else {
      return item;
    }
  }

  @Memoize()
  contentText(): string {
    if (this.fieldConfig.select && this.fieldConfig.select.contentTextType) {
      let contentTextType = this.fieldConfig.select.contentTextType;

      if (contentTextType) {
        if (contentTextType === SelectContentText.CUSTOM_TEXT && this.fieldConfig.select.contentText) {
          return this.fieldConfig.select.contentText;
        }
        if (contentTextType === SelectContentText.MORE_OPTIONS) {
          if (this.control.value) {
            if (this.control.value.length === 0) {
              this.animation = false;
              return '';
            } else {
              this.animation = true;
              return `Wybrano ${this.control.value.length} ${this.control.value.length > 4 ? 'opcji' : 'opcje'}`
            }
          } else {
            this.animation = false;
            return '';
          }
        }
        if (contentTextType === SelectContentText.CAPTION_TEXT) {
          this.placeholder = this.fieldConfig.caption;
          if (this.control.value) {
            this.animation = true;
          } else {
            this.animation = false;
          }
        }
      }
    }
  }

  get isMobile(): boolean {
    return isMobile();
  }

  get showValueLabel(): boolean {
    return isNotNullOrUndefined(this.control.value) && !this.loading;
  }

  get showLoadingLabel(): boolean {
    return isNotNullOrUndefined(this.control.value) && this.loading;
  }

  get isInputFulfilled(): boolean {
    return !!this.control.value;
  }

  get isAsync(): boolean {
    return this.fieldConfig.select.async;
  }

  get shouldDeliverItemsEvent(): boolean {
    return (this.loading && !this.isAsync) || this.fieldConfig.select.valueBasedInitialDictionary;
  }

  // commented line is definition of original requirements for displaying verification hint
  get verificationHintVisible(): boolean {
    return false;
    // return this.isPredefinedValue && !this.errorsMap.get(this.fieldConfig.fieldKey);
  }

  get typeToSearchTextVisible(): boolean {
    return this.isFocused && !!this.typeToSearchText;
  }

  get selectValueIfOnlyOneItemTimeout(): number {
    return this.fieldConfig.select.hasOwnProperty('selectValueIfOnlyOneItemTimeout') ? this.fieldConfig.select.selectValueIfOnlyOneItemTimeout : 0;
  }

  get isSafari(): boolean {
    return this.deviceService.isSafari;
  }

  get itemValueProperty(): string | null {
    return this.fieldConfig.options ? this.fieldConfig.options.item : null;
  }

  get itemLabelProperty(): string | null {
    const { options } = this.fieldConfig;
    if (options && options.label) {
      const fieldOptionLabel = <FieldOptionLabel>options.label;
      if (_.isString(fieldOptionLabel)) {
        return <any>fieldOptionLabel;
      }
      if (fieldOptionLabel.labelConfig && !!fieldOptionLabel.labelConfig.bindLabel) {
        return fieldOptionLabel.labelConfig.bindLabel;
      } else if (isNotNullOrUndefined(fieldOptionLabel.label) && _.isString(fieldOptionLabel.label)) {
        return <string>fieldOptionLabel.label;
      }
    }
    return null;
  }

  get suffix(): string | null {
    if (this.fieldConfig.options && this.fieldConfig.options.label && this.fieldConfig.options.label.labelConfig) {
      return this.fieldConfig.options.label.labelConfig.suffix;
    }
    return null;
  }

  get shouldIncludeValueInClickEvent(): boolean {
    return isNotNullOrUndefined(this.fieldConfig.initialValue) && !this.formFieldService.fieldClickEventSent(this.fieldConfig);
  }

  get hasCustomIcon(): boolean {
    return isNotNullOrUndefined(this.customIcon);
  }

  get customIconVisible(): boolean {
    return this.hasCustomIcon && this.control.enabled;
  }

  async valueLabel() {
    const selectedItem = await this.getSelectedItem();
    if (isNotNullOrUndefined(selectedItem)) {
      const value = isNotNullOrUndefined(this.itemLabelProperty) ? selectedItem[this.itemLabelProperty] : selectedItem;
      return this.suffix ? `${value}${this.suffix}` : value;
    }
    return null;
  }

  open(): void {
    this.openedProgrammatically = true;
    this.select.open();
  }

  submitValues(): void {
    this.saveViaButton = true;
    this.prevTmpControlValues = this.control.value;
    this.$propagateValues.emit({ fieldKey: this.fieldConfig.fieldKey, control: this.control });
    this.select.close();
  }

  async openSelectEvent() {
    this.opened.emit();
    if (isMobile()) {
      if (isIOS()) {
        setTimeout(() => {
          this.handleIOSMobileOpen()
        }, 0);
      } else {
        if (this.isAsync) {
          await this.openModalSelectDialog(this.items);
        } else {
          this.openModalSelectDialog(this.fieldConfig.options.items);
        }
      }
    }
    this.onOpen();
    this.initSearchable(); // update searchable from config
  }

  closeSelectEvent(): void {
    this.openedProgrammatically = false;
    if (this.isSafari && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    if (this.fieldConfig.select.closeEvent) {
      this.fieldConfig.select.closeEvent.emit();
    }
    if ((this.prevTmpControlValues !== this.control.value) === this.clearOnClose) {
      this.control.patchValue(this.prevTmpControlValues)
    }
    this.saveViaButton = false;
    this.$closeEvent.emit();
    this.onChange(null);
    this.onClose();
    this.hideLabel = false;
    this.searchable = false;
  }

  searchSelectEvent(selectSearchEvent: {term: string, items: any[]}): void {
    if (this.fieldConfig.select.searchEvent) {
      this.fieldConfig.select.searchEvent.emit(selectSearchEvent.term);
    }
    if (this.autoSelectOnSearch) {
      this.handleAutoSelectOnSearch(selectSearchEvent);
    }
  }

  closeSelect(valueSelected: boolean = true) {
    this.select.close();
    if (!this.select.hasValue) {
      this.select.element.classList.remove('ng-select-focused');
    }
    this.focusOut();
  }

  clearSelectedValues(): void {
    this.control.patchValue([]);
    this.submitValues();
  }

  onFocus() {
    this.blurSelect();
    this.isFocused = true;
    if (!isMobile()) {
      this.select.open();
    } else {
      if (!this.openedProgrammatically) {
        this.onClick();
      }
    }
  }

  focusOut() {
    this.isFocused = false;
  }

  onOpen() {
    this.hideLabel = true;
    super.onOpen();
  }

  async onClick(event?: PointerEvent) {
    const isOpenClick = event ? event.target['classList'].contains('ng-arrow-wrapper') : true;
    if (isOpenClick) {
      const value = this.shouldIncludeValueInClickEvent ? await this.valueLabel() : null;
      this.formFieldService.onClick(this.fieldConfig, value);
      this.emitInteractiveClick();
    }
  }

  async onSelectChange() {
    const value = await this.valueLabel();
    this.formFieldService.onClick(this.fieldConfig, value);
  }

  setNotFoundText(text: string): void {
    this.notFoundText = text ? text : 'SELECT_COMPONENT.notFoundText';
  }

  private initItems(): void {
    if (this.fieldConfig.select.dataType === 'promise') {
      if (this.items === this.fieldConfig.options.items) {
        this.items = this.fieldConfig.options.items
      } else {
        this.items = new Promise((resolve) => [])
      }
    }
    if (isNullOrUndefined(this.fieldConfig.select.dataType) || this.fieldConfig.select.dataType === 'array') {
      this.items = this.fieldConfig.options.items;
    }
  }

  private handleIOSMobileOpen() {
    setTimeout(() => this.select.element.blur(), 0);
    setTimeout(async () => {
      if (this.isAsync) {
        await this.openModalSelectDialog(this.items);
      } else {
        this.openModalSelectDialog(this.fieldConfig.options.items);
      }
    }, 1);
  }

  private openModalSelectDialog(list) {
    const selectDialogConfig = {
      list,
      controlValue: this.control.value,
      fieldConfig: this.fieldConfig,
      translateLabel: this.translateLabel,
      selectSearchVisible: this.selectSearchVisible,
      getConcatLabel: (item, label) => this.getConcatLabel(item, label),
      itemsEvent: this.shouldDeliverItemsEvent ? this.itemsEvent : null,
      dynamicDictionaryEvent: this.dynamicDictionaryEvent,
      loadingText: this.loadingText,
      templateOptions: this.templateOptions,
    };

    if (this.fieldConfig.select.sharedSelectDialog && this.selectDialogRef && this.selectDialogRef.componentInstance) {
      this.selectDialogRef.componentInstance.data = selectDialogConfig;
      this.selectDialogRef.componentInstance.init();
    } else {
      this.selectDialogRef = this.dialog.open(SelectDialogComponent, SelectDialogComponent.getConfigBuilder().buildConfiguration(selectDialogConfig));
      this.selectDialogRefUpdate.emit(this.selectDialogRef);
    }

    if (this.fieldConfig.select && this.fieldConfig.select.valueSelectedEvent) {
      this.fieldConfig.select.valueSelectedEvent.subscribe(value => {
        this.closeSelect(isNotNullOrUndefined(value));
        this.select.close();

        if (isNotNullOrUndefined(value) && value !== this.control.value) {
          this.control.setValue(value);
        }

        if (isNotNullOrUndefined(value)) {
          this.onSelectChange();
        }
      });
    }

    this.selectDialogRef.afterOpened().subscribe(() => this.blurSelect());

    this.selectDialogRef.afterClosed().subscribe(value => {
      this.closeSelect(isNotNullOrUndefined(value));
      this.select.close();

      this.selectDialogRefUpdate.emit(null);

      // force removing focus on active element after select modal was closed - fix for inputs on mobile
      if (this.isMobile) {
        // do poprawki, po zamykaniu selecta był szary ekran
        // this.renderer.selectRootElement(this.elementRef.nativeElement.ownerDocument.activeElement).blur();
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
      }

      if (isNotNullOrUndefined(value) && value !== this.control.value) {
        this.control.setValue(value);
      }

      if (isNotNullOrUndefined(value)) {
        this.onSelectChange();
      }

    });
  }

  private blurSelect() {
    if (this.deviceService.isMobileDevice) {
      this.select.element.blur();
      setTimeout(() => this.select.element.blur(), 0);

      if (document.activeElement instanceof HTMLElement && !this.fieldConfig.select.dynamicDictionary) {
        document.activeElement.blur();
      }
    }
  }


  private getLabelForArray(item: any, resultLabel): string {
    let key;

    try {
      key = this.findObjectTranslationKey(item);
    } catch (e) {
      key = 'value'
    }

    return this.languageService.getTranslationForKey(resultLabel, key);
  }

  private findObjectTranslationKey(item): string {
    return Object.keys(item.values[0]).filter(value => value !== 'languageCode')[0];
  }

  private attachSearchFn() {
    if (this.fieldConfig.options.label) {
      this.select.searchFn = (term: string, item: any) => {
        term = term.toLocaleLowerCase();
        if (this.fieldConfig.options.item) {
          const searchProperty = this.fieldConfig.options.label.label;
          if (isArray(searchProperty)) {
            for (let property of searchProperty) {
              const value = typeof item[property] === 'string' ? item[property] : String(item[property]);
              if (isNotNullOrUndefined(item[property]) && (value.toLocaleLowerCase().indexOf(term) > -1 || value.toLocaleLowerCase() === term)) {
                return true;
              }
            }
            return false;
          } else if (Array.isArray(item[searchProperty])) {
            const key = this.findObjectTranslationKey(item);
            const index = item[searchProperty]
                .map(({languageCode}) => languageCode.toLocaleLowerCase())
                .indexOf(this.languageService.getDefaultLanguage());
            return item[searchProperty][index][key].toLocaleLowerCase().indexOf(term) >= 0;
          } else {
            const value = this.translateLabel ? this.translateService.instant(item[searchProperty]) : item[searchProperty];
            return value.toLocaleLowerCase().indexOf(term) > -1
                || value.toLocaleLowerCase() === term;
          }
        } else {
          let value = typeof item === 'string' ? item : String(item);
          if (this.fieldConfig.options.label && this.fieldConfig.options.label.labelConfig && this.fieldConfig.options.label.labelConfig.suffix) {
            value += this.fieldConfig.options.label.labelConfig.suffix;
          }
          if (isNotNullOrUndefined(item) && (value.toLocaleLowerCase().indexOf(term) > -1 || value.toLocaleLowerCase() === term)) {
            return true;
          }
          return false;
        }
      };
    }
  }

  private initValueChangeControl() {
    if (!this.multiple) {
      this.valueChanges$ = this.control.valueChanges.subscribe(value => {
        if (isNotNullOrUndefined(value)) {
          this.select.close();
        }
      })
    }
  }

  private initLoadingEvent() {
    let loadingEvent = this.fieldConfig.select.loadingEvent;
    if (loadingEvent) {
      loadingEvent.subscribe((value) => {
        this.loading = value;
        if (!this.loading) {
          if (this.isAsync) {
            this.initItems();
          }
          if (this.fieldConfig.select.dynamicDictionary) {
            this.dynamicDictionaryEvent.emit({ items: this.fieldConfig.options.items, noResultsText: null });
          } else {
            if (this.isAsync) { // dictionary is a promise
              this.fieldConfig.options.items.then(items => this.itemsEvent.emit(items));
            } else { // dictionary is an array
              this.itemsEvent.emit(this.fieldConfig.options.items);
            }
          }
          if (!this.select.isOpen && this.isFocused) {
            this.select.open();
          }
          if (!this.control.disabled && isNullOrUndefined(this.control.value)) {
            this.initValue();
          }
        }
      });
    }
  }

  private initSearchable(): void {
    if (!isNullOrUndefined(this.fieldConfig.select.searchable)) {
      this.searchable = this.fieldConfig.select.searchable;
    }
  }

  private initDropdownPosition(): void {
    if (!isNullOrUndefined(this.fieldConfig.select.dropdownPosition)) {
      this.dropdownPosition = this.fieldConfig.select.dropdownPosition;
    }
  }

  private initClearOnClose(): void {
    if (!isNullOrUndefined(this.fieldConfig.select.clearOnClose)) {
      this.clearOnClose = this.fieldConfig.select.clearOnClose;
    }
  }

  private initMultiple() {
    if (!isNullOrUndefined(this.fieldConfig.select.multiple)) {
      this.multiple = this.fieldConfig.select.multiple;
    }
  }

  private initCloseOnSelect() {
    if (!isNullOrUndefined(this.fieldConfig.select.closeOnSelect)) {
      this.closeOnSelect = this.fieldConfig.select.closeOnSelect;
    }
  }

  private initClearable() {
    if (!isNullOrUndefined(this.fieldConfig.select.clearable)) {
      this.clearable = this.fieldConfig.select.clearable;
    }
  }

  private initNotFoundText() {
    if (!isNullOrUndefined(this.fieldConfig.select.notFoundText)) {
      this.notFoundText = this.fieldConfig.select.notFoundText;
    } else {
      this.notFoundText = 'SELECT_COMPONENT.notFoundText';
    }
  }

  private initTypeToSearchText() {
    if (isNotNullOrUndefined(this.fieldConfig.select.typeToSearchText)) {
      this.typeToSearchText = this.fieldConfig.select.typeToSearchText;
      this.selectSearchText = this.fieldConfig.select.typeToSearchText;
    }
  }

  private initAutoSelectOnSearch() {
    if (isNotNullOrUndefined(this.fieldConfig.select.autoSelectOnSearch)) {
      this.autoSelectOnSearch = this.fieldConfig.select.autoSelectOnSearch;
    }
  }

  private handleAutoSelectOnSearch(searchEvent: any) {
    const { items, term } = searchEvent;
    if (items && items.length === 1) {
      const itemLabel = this.itemLabelProperty ? items[0][this.itemLabelProperty] : items[0];
      const itemLabelUpperCased = _.isString(itemLabel) ? itemLabel.toUpperCase() : (itemLabel + '').toUpperCase();
      const searchLabel = term ? term.toUpperCase() : '';
      if (itemLabelUpperCased === searchLabel) {
        const value = this.fieldConfig.options.item ? items[0][this.fieldConfig.options.item] : items[0];
        this.control.setValue(value);
      }
    }
  }

  private initValue(): void {
    if (!isNullOrUndefined(this.fieldConfig.select.setValueIfOnlyOneItem) && this.fieldConfig.select.setValueIfOnlyOneItem) {
      let items = this.fieldConfig.options.items;
      if (this.isAsync) {
        items.then(arr => this.setValueIfOnlyOneItem(arr));
      } else {
        this.setValueIfOnlyOneItem(items);
      }
    }
  }

  private setValueIfOnlyOneItem(items: any[]) {
    if (!this.isMobile && items && items.length === 1) {
      const item = items[0];
      const value = this.fieldConfig.options.item ? item[this.fieldConfig.options.item] : item;
      if (this.selectValueIfOnlyOneItemTimeout) {
        setTimeout(() => this.control.setValue(value), this.selectValueIfOnlyOneItemTimeout);
      } else {
        this.control.setValue(value);
      }
    }
  }

  private initSelectSearchVisible(innerWidth: number): void {
    this.selectSearchVisible = isMobile(innerWidth);
  }

  private initAnimationEnabled(): void {
    if (isNotNullOrUndefined(this.fieldConfig.select.animationEnabled)) {
      this.animationEnabled = this.fieldConfig.select.animationEnabled;
    }
  }

  private initCustomIcon(): void {
    if (isCarOne()) {
      this.customIcon = 'arrow_back_ios';
    }

    if (this.fieldConfig.select && isNotNullOrUndefined(this.fieldConfig.select.customIcon)) {
      this.customIcon = this.fieldConfig.select.customIcon;
    }
  }

  private async getItems() {
    let items = (this.isAsync && isPromise(this.items)) ? await this.items : this.items;
    if (!isNotNullOrUndefined(items) || items.length === 0) {
      items = (this.isAsync && isPromise(this.fieldConfig.options.items)) ? await this.fieldConfig.options.items : this.fieldConfig.options.items;
    }
    return items;
  }

  private async getSelectedItem() {
    if (this.isInputFulfilled) {
      const items = await this.getItems();
      if (!isNotNullOrUndefined(items) || items.length === 0) {
        return this.select.selectedValues ? this.select.selectedValues[0] : null;
      }
      return items.find(item => {
        const value = this.itemValueProperty ? item[this.itemValueProperty] : item;
        return value === this.control.value;
      });
    }
    return null;
  }
}

export interface TemplateOptions {
  eurotax: string;
  infoexpert: string;
  model: string;
}
